// /app/frontend/src/components/frugal-v2/components/reports/ComparisonSections.jsx
// Previous month comparison + 12-month accumulated charts

import React from 'react';
import { BarChart3, Layers } from 'lucide-react';
import ReportSection from './ReportSection';

export const PreviousMonthCompareSection = ({ data, t, catNames }) => {
  if (!data?.by_category?.length) return null;
  const totals = data.totals || {};
  const totalDiff = totals.diff || 0;
  const totalPercent = totals.percent || 0;

  return (
    <ReportSection title={t.vsPreviousMonth} icon={BarChart3}>
      <div className="space-y-2">
        {data.by_category.map((row, idx) => {
          const positive = row.diff < 0;
          return (
            <div key={idx} className="bg-white/5 rounded-lg p-3" data-testid={`prev-cmp-${idx}`}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-white font-medium text-sm">{catNames[row.category] || row.category}</span>
                <span className={`text-xs font-semibold ${positive ? 'text-emerald-400' : row.diff > 0 ? 'text-rose-400' : 'text-slate-400'}`}>
                  {row.diff > 0 ? '+' : ''}{row.percent.toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between text-xs text-slate-400 flex-wrap gap-1">
                <span>{t.current}: <span className="text-slate-200">${row.current.toFixed(2)}</span></span>
                <span>{t.previous}: <span className="text-slate-200">${row.previous.toFixed(2)}</span></span>
                <span>{t.diff}: <span className={positive ? 'text-emerald-400' : row.diff > 0 ? 'text-rose-400' : 'text-slate-200'}>{row.diff > 0 ? '+' : ''}${row.diff.toFixed(2)}</span></span>
              </div>
            </div>
          );
        })}
        <div className="bg-gradient-to-r from-sky-500/10 to-emerald-500/10 border border-sky-500/30 rounded-lg p-3 mt-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-white font-semibold text-sm">Total</span>
            <span className={`text-sm font-bold ${totalDiff < 0 ? 'text-emerald-400' : totalDiff > 0 ? 'text-rose-400' : 'text-slate-200'}`}>
              {totalDiff > 0 ? '+' : ''}{totalPercent.toFixed(1)}%
            </span>
          </div>
          <div className="flex justify-between text-xs text-slate-300 flex-wrap gap-1">
            <span>{t.current}: <strong>${(totals.current || 0).toFixed(2)}</strong></span>
            <span>{t.previous}: <strong>${(totals.previous || 0).toFixed(2)}</strong></span>
            <span>{t.diff}: <strong>{totalDiff > 0 ? '+' : ''}${totalDiff.toFixed(2)}</strong></span>
          </div>
        </div>
      </div>
    </ReportSection>
  );
};

export const Accumulated12mSection = ({ data, t, catNames }) => {
  if (!data?.items?.length) return null;
  return (
    <ReportSection title={t.accumulated12m} icon={Layers}>
      <div className="space-y-2">
        {data.items.map((row, idx) => (
          <div key={idx} className="bg-white/5 rounded-lg p-3" data-testid={`acc12m-${idx}`}>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-white font-medium text-sm">{catNames[row.category] || row.category}</span>
              <span className="text-xs text-slate-400">{row.percentage.toFixed(1)}%</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-sky-500 to-emerald-500" style={{ width: `${Math.min(row.percentage, 100)}%` }} />
            </div>
            <div className="flex justify-between mt-1.5 text-xs">
              <span className="text-slate-400">{t.monthlyAvg}: <span className="text-slate-200">${row.monthly_avg.toFixed(2)}</span></span>
              <span className="text-slate-200 font-medium">${row.total.toFixed(2)}</span>
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between pt-2 font-semibold border-t border-white/10">
          <span className="text-sky-400">{t.grandTotal}</span>
          <div className="text-right">
            <div className="text-white">${(data.grand_total || 0).toFixed(2)}</div>
            <div className="text-xs text-slate-400 font-normal">
              {t.monthlyAvg}: ${(data.monthly_average_total || 0).toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    </ReportSection>
  );
};
